import { useEffect, useState } from 'react';

import type { ISelectItem } from '@treneo/models';
import { useAppTranslations } from '@treneo/shared/i18n';
import { Autocomplete, TextField } from '@treneo/shared/ui';

import { useOrganizationTrainersByDaDesktopIdQuery } from '../../../../data';

type TrainerSelectContentProps = Readonly<{
  organizationId: number | null;
  value: ISelectItem | null;
  onChange: (trainer: ISelectItem | null) => void;
  disabled?: boolean;
  trainers: ISelectItem[];
  isLoadingTrainers: boolean;
}>;

const TrainerSelectContent = ({
  organizationId,
  value,
  onChange,
  disabled = false,
  trainers,
  isLoadingTrainers
}: TrainerSelectContentProps) => {
  const { t } = useAppTranslations();
  const [searchText, setSearchText] = useState('');
  const [hasOpened, setHasOpened] = useState(false);

  useEffect(() => {
    if (organizationId != null) {
      setHasOpened(false);
    }
  }, [organizationId]);

  const handleOpen = () => {
    if (!hasOpened && organizationId != null) {
      setHasOpened(true);
    }
  };

  const filteredTrainers = trainers.filter(trainer => {
    if (!searchText.trim()) {
      return true;
    }
    const searchLower = searchText.toLowerCase();
    return trainer.label.toLowerCase().includes(searchLower);
  });

  return (
    <Autocomplete
      options={filteredTrainers}
      value={value}
      onChange={(_, newValue) => onChange(newValue)}
      onInputChange={(_, newInputValue) => setSearchText(newInputValue)}
      onOpen={handleOpen}
      getOptionLabel={trainer => trainer?.label ?? ''}
      loading={isLoadingTrainers}
      disabled={disabled || !organizationId}
      renderInput={params => (
        <TextField
          {...params}
          label={t('trainers.selectTrainer')}
          placeholder={t('trainers.searchTrainers')}
          fullWidth
          slotProps={{ inputLabel: { shrink: true } }}
        />
      )}
      renderOption={(props, trainer) => (
        <li {...props} key={trainer.id}>
          {trainer.label}
        </li>
      )}
      noOptionsText={t('trainers.noTrainersFound')}
      isOptionEqualToValue={(option, val) => option.id === val.id}
    />
  );
};

type TrainerSelectWithQueryProps = Readonly<{
  organizationId: number | null;
  value: ISelectItem | null;
  onChange: (trainer: ISelectItem | null) => void;
  disabled?: boolean;
}>;

const TrainerSelectWithQuery = ({
  organizationId,
  value,
  onChange,
  disabled = false
}: TrainerSelectWithQueryProps) => {
  const effectiveOrganizationId = organizationId ?? 0;
  const { data, isLoading } = useOrganizationTrainersByDaDesktopIdQuery(effectiveOrganizationId, {
    enabled: effectiveOrganizationId > 0
  });
  const trainers = (data?.trainers ?? []) as ISelectItem[];

  return (
    <TrainerSelectContent
      organizationId={organizationId}
      value={value}
      onChange={onChange}
      disabled={disabled}
      trainers={trainers}
      isLoadingTrainers={isLoading}
    />
  );
};

type Props = Readonly<{
  organizationId: number | null;
  value: ISelectItem | null;
  onChange: (trainer: ISelectItem | null) => void;
  disabled?: boolean;
  trainers?: ISelectItem[];
  isLoadingTrainers?: boolean;
}>;

export const TrainerSelect = ({
  organizationId,
  value,
  onChange,
  disabled = false,
  trainers: trainersProp,
  isLoadingTrainers: isLoadingTrainersProp
}: Props) => {
  const useTrainersFromProps = trainersProp !== undefined;

  if (useTrainersFromProps) {
    return (
      <TrainerSelectContent
        organizationId={organizationId}
        value={value}
        onChange={onChange}
        disabled={disabled}
        trainers={trainersProp ?? []}
        isLoadingTrainers={isLoadingTrainersProp ?? false}
      />
    );
  }

  return (
    <TrainerSelectWithQuery
      organizationId={organizationId}
      value={value}
      onChange={onChange}
      disabled={disabled}
    />
  );
};
